import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
	getCustomers,
	getItems,
	getProducts,
	getRentals
} from "../service/callAPI";
import { cacheCustomers1, cacheProducts1 } from "../service/cache";

export const useLoadState = (fetcher: any | undefined, callback: Function) => {
	useEffect(() => {
		loadState();

		async function loadState() {
			if (!fetcher) {
				return;
			}
			try {
				const response = await fetcher();
				if (!response) {
					return;
				}
				callback(response);
			} catch (e: any) {
				const message = e?.response?.data || e.toString();
				toast.error(message);
			}
		}
	}, [fetcher, callback]);
};

export const loadData = async (
	fetcher: any | undefined,
	callback: Function
) => {
	if (!fetcher) {
		return;
	}
	try {
		const response = await fetcher;
		if (!response) {
			return;
		}
		callback(response);
	} catch (e: any) {
		const message = e?.response?.data || e.toString();
		toast.error(message);
	}
};

export const useEventListener = (eventName: string, eventHandler: any) => {
	useEffect(() => {
		document.addEventListener(eventName, eventHandler);
		return () => {
			document.removeEventListener(eventName, eventHandler);
		};
	}, [eventHandler, eventName]);
};

export const useCacheCustomers = () => {
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		cacheCustomers();
	}, []);

	async function cacheCustomers() {
		try {
			setIsLoading(true);
			const customers = await getCustomers({ page: 1, limit: -1 });
			localStorage.setItem("customers", JSON.stringify(customers.customers));
		} catch (e: any) {
			toast.error(`Lỗi cacheCustomers|${e.toString()}`);
		} finally {
			setIsLoading(false);
		}
	}

	return { isLoading };
};

export const useCatchRentals = () => {
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		cacheRentals();
	}, []);

	async function cacheRentals() {
		try {
			setIsLoading(true);
			const rentals = await getRentals({ page: 1, limit: -1 });

			const dbName = "rentalsDB";
			const dbVersion = 1;
			const request = indexedDB.open(dbName, dbVersion);

			request.onerror = (event: any) => {
				console.error("IndexedDB error:", event.target.errorCode);
			};

			request.onupgradeneeded = (event: any) => {
				const db = event.target.result;
				db.createObjectStore("rentals", { keyPath: "id" });
			};

			request.onsuccess = (event: any) => {
				const db = event.target.result;
				const transaction = db.transaction(["rentals"], "readwrite");
				const store = transaction.objectStore("rentals");
				rentals.rentals.forEach((item: any) => {
					store.put(item);
				});
			};
		} catch (e: any) {
			toast.error(`Lỗi catchRentals|${e.toString()}`);
			setIsLoading(false);
		}
	}

	return { isLoading };
};

export const useCacheProducts = () => {
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		cacheProducts();
	}, []);

	async function cacheProducts() {
		try {
			setIsLoading(true);
			const products = await getProducts({ page: 1, limit: -1 });

			const dbName = "productsDB";
			const dbVersion = 1;
			const request = indexedDB.open(dbName, dbVersion);

			request.onerror = (event: any) => {
				console.error("IndexedDB error:", event.target.errorCode);
			};

			request.onupgradeneeded = (event: any) => {
				const db = event.target.result;
				db.createObjectStore("products", { keyPath: "id" });
			};

			request.onsuccess = (event: any) => {
				const db = event.target.result;
				const transaction = db.transaction(["products"], "readwrite");
				const store = transaction.objectStore("products");
				products.products.forEach((item: any) => {
					store.put(item);
				});
			};
		} catch (e: any) {
			toast.error(`Lỗi cacheProducts|${e.toString()}`);
		} finally {
			setIsLoading(false);
		}
	}

	return { isLoading };
};

export const useCacheDataForSearching = () => {
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		cacheCustomers();
	}, []);

	async function cacheCustomers() {
		try {
			setIsLoading(true);
			const customers = await getCustomers({ page: 1, limit: -1 });
			const products = await getProducts({ page: 1, limit: -1 });
			const rentals = await getRentals({ page: 1, limit: -1 });
			const items = await getItems();

			//cache customers in localStorage
			localStorage.setItem("customers", JSON.stringify(customers.customers));

			const findProductByItemId = (itemId: string) => {
				const item = items.items.find((i: any) => i.id === itemId);
				return products.products.find((p) => p.id === item?.productId);
			};

			const enrichedRentals = rentals.rentals.map((item: any) => {
				const productByItems = item.items.map((i: any) => {
					return findProductByItemId(i.itemId);
				});

				const customer = customers.customers.find(
					(c: any) => c.id === item.customerId
				);

				return {
					...item,
					productByItems,
					customer
				};
			});

			const reMapping = enrichedRentals.map((item: any) => {
				const productKey = item?.productByItems
					?.map(
						(p: any) => `${p?.name} ${p?.sku} ${p?.price} ${p?.description}`
					)
					.join(" ");
				const searchKey = `${item.customer?.name} ${item.customer?.phone} ${item.customer?.address} ${item.customer?.email} ${productKey}`;

				return {
					...item,
					searchKey
				};
			});

			const dbName = "rentalsDB";
			const dbVersion = 1;
			const request = indexedDB.open(dbName, dbVersion);

			request.onerror = (event: any) => {
				console.error("IndexedDB error:", event.target.errorCode);
			};

			request.onupgradeneeded = (event: any) => {
				const db = event.target.result;
				const objectStore = db.createObjectStore("rentals", { keyPath: "id" });
				objectStore.createIndex("searchKeyIndex", "searchKey");
			};

			request.onsuccess = (event: any) => {
				const db = event.target.result;
				const transaction = db.transaction(["rentals"], "readwrite");
				const store = transaction.objectStore("rentals");
				reMapping.forEach((item: any) => {
					store.put(item);
				});
			};
		} catch (e: any) {
			toast.error(`Lỗi cache data|${e.toString()}`);
		} finally {
			setIsLoading(false);
		}
	}

	return { isLoading };
};

export const useSearchRentals = (searchKey: string) => {
	const [isLoading, setIsLoading] = useState(true);
	const [rentals, setRentals] = useState([]);

	useEffect(() => {
		const searchRentals = async () => {
			setIsLoading(true);
			const dbName = "rentalsDB";
			const dbVersion = 1;
			const request = indexedDB.open(dbName, dbVersion);

			request.onerror = (event: any) => {
				console.error("IndexedDB error:", event.target.errorCode);
			};

			request.onsuccess = (event: any) => {
				const db = event.target.result;
				const transaction = db.transaction(["rentals"], "readonly");
				const store = transaction.objectStore("rentals");
				const index = store.index("searchKeyIndex");

				const searchRegex = new RegExp(searchKey, "i");
				const searchQuery = index.openCursor(IDBKeyRange.lowerBound(""));

				const matchingRentals: any = [];
				searchQuery.onsuccess = (event: any) => {
					const cursor = event.target.result;
					if (cursor) {
						if (searchRegex.test(cursor.value.searchKey)) {
							matchingRentals.push(cursor.value);
						}
						cursor.continue();
					} else {
						setRentals(matchingRentals);
						setIsLoading(false);
					}
				};
			};
		};

		if (searchKey) {
			searchRentals();
		} else {
			setRentals([]);
			setIsLoading(false);
		}
	}, [searchKey]);

	return { isLoading, rentals };
};

export const useCacheProducts1 = () => {
	useEffect(() => {
		cacheProducts1();
	}, []);
};

export const useCacheCustomers1 = () => {
	useEffect(() => {
		cacheCustomers1();
	}, []);
};
